import {
    asyncToGenerator,
    checkError,
    convertToUnsigned,
    toNumber,
    _extends,
} from './utils';
import Eth, { ledgerService } from '@ledgerhq/hw-app-eth';
import { BigNumber, providers } from 'ethers';
import invariant from 'tiny-invariant';

const transactions = require('@ethersproject/transactions');
const strings = require('@ethersproject/strings');
const bytes = require('@ethersproject/bytes');
const abstractSigner = require('@ethersproject/abstract-signer');

const _interopNamespace = (e) => {
    if (e && e.__esModule) {
        return e;
    } else {
        const n = {};
        if (e) {
            Object.keys(e).forEach(function (k) {
                const d = Object.getOwnPropertyDescriptor(e, k);
                Object.defineProperty(
                    n,
                    k,
                    d.get
                        ? d
                        : {
                              enumerable: true,
                              get: function () {
                                  return e[k];
                              },
                          }
                );
            });
        }
        n['default'] = e;
        return n;
    }
};

class LedgerHQSigner extends abstractSigner.Signer {
    constructor(provider, getPath) {
        super();
        this._index = 0;
        this._address = '';
        this.getPath = getPath;
        this.provider = provider;
    }

    withEthApp(callback) {
        const _this = this;

        return asyncToGenerator(function* () {
            const transport = yield _this.provider.getTransport();

            try {
                const eth = new Eth(transport);

                if (!_this.path) {
                    const selectedPath = yield _this.getPath(eth);
                    _this.path = selectedPath;
                }

                yield eth.getAppConfiguration();
                return yield callback(eth);
            } catch (error) {
                return checkError(error);
            } finally {
                yield transport.close();
            }
        })();
    }
    getAddress() {
        const _this = this;

        return asyncToGenerator(function* () {
            if (!_this._address) {
                const account = yield _this.withEthApp((eth) => {
                    return eth.getAddress(_this.path);
                });

                const address = _this.provider.formatter.address(account.address);

                _this._address = address;
            }

            return _this._address;
        })();
    }
    signMessage(message) {
        const _this = this;

        return asyncToGenerator(function* () {
            if (typeof message === 'string') {
                message = strings.toUtf8Bytes(message);
            }

            var messageHex = bytes.hexlify(message).substring(2);
            var sig = yield _this.withEthApp((eth) =>
                eth.signPersonalMessage(_this.path, messageHex)
            );
            sig.r = '0x' + sig.r;
            sig.s = '0x' + sig.s;
            return bytes.joinSignature(sig);
        })();
    }

    populateUnsigned(transaction) {
        const _this = this;

        return asyncToGenerator(function* () {
            var populated = yield _this.populateTransaction(transaction);
            var nonce = toNumber(populated.nonce);

            if (populated.type === 0) {
                var { gasLimit, type, chainId, gasPrice, value, data, to } = populated; // Allowed transaction keys for Legacy and EIP-155 Transactions

                return {
                    gasLimit,
                    type,
                    chainId,
                    gasPrice,
                    nonce,
                    value,
                    data,
                    to,
                };
            }

            return _extends({}, populated, {
                nonce,
            });
        })();
    }

    signTransaction(transaction, loadConfig, resolutionConfig) {
        const _this = this;

        return asyncToGenerator(function* () {
            if (loadConfig === void 0) {
                loadConfig = {};
            }

            if (resolutionConfig === void 0) {
                resolutionConfig = {};
            }

            var unsignedTx = yield convertToUnsigned(transaction);
            var populatedTx = yield _this.populateUnsigned(unsignedTx);
            var serializedTx = transactions.serialize(populatedTx).substring(2);
            var resolution = yield ledgerService.resolveTransaction(
                serializedTx,
                loadConfig,
                resolutionConfig
            );
            var sig = yield _this.withEthApp((eth) =>
                eth.signTransaction(_this.path, serializedTx, resolution)
            );
            return transactions.serialize(populatedTx, {
                v: BigNumber.from('0x' + sig.v).toNumber(),
                r: '0x' + sig.r,
                s: '0x' + sig.s,
            });
        })();
    }

    connect(provider) {
        return new LedgerHQSigner(provider, this.path);
    }

    connectUnchecked() {
        throw new Error('method is not implemented');
    }

    sendUncheckedTransaction() {
        throw new Error('method is not implemented');
    }

    unlock() {
        //removed Generator function
        throw new Error('method is not implemented');
    }

    _legacySignMessage() {
        //removed Generator function
        throw new Error('method is not implemented');
    }

    _signTypedData() {
        //removed Generator function
        throw new Error('method is not implemented');
    }
}

class LedgerHQProvider extends providers.JsonRpcBatchProvider {
    constructor(chainId, url, getPath) {
        super(chainId, url);
        this.getPath = getPath;
    }

    getSigner() {
        return new LedgerHQSigner(this, this.getPath);
    }

    async listAccounts() {
        throw new Error('method is not implemented');
    }

    detectNetwork() {
        return this._network;
    }

    getTransport() {
        const _this = this;

        return asyncToGenerator(function* () {
            !_this.transport ? invariant(false, 'Transport is not defined') : void 0;

            try {
                let _thisTransport;

                var transport = yield (_thisTransport = _this.transport) == null
                    ? void 0
                    : _thisTransport.create();
                _this.device = transport.device;
                return transport;
            } catch (error) {
                return checkError(error);
            }
        })();
    }
    enable() {
        const _this = this;

        return asyncToGenerator(function* () {
            try {
                const { default: TransportHID } = yield new Promise(function (resolve) {
                    resolve(_interopNamespace(require('@ledgerhq/hw-transport-webhid')));
                });
                _this.transport = TransportHID;
                const { hid } = window.navigator;

                const onDisconnect = (event) => {
                    if (_this.device === event.device) {
                        hid.removeEventListener('disconnect', onDisconnect);

                        _this.emit('disconnect');
                    }
                };

                hid.addEventListener('disconnect', onDisconnect);

                if (!_this.signer) {
                    _this.signer = _this.getSigner();
                }

                return yield _this.getAddress();
            } catch (error) {
                return checkError(error);
            }
        })();
    }

    getAddress() {
        const _this = this;

        return asyncToGenerator(function* () {
            !_this.signer ? invariant(false, 'Signer is not defined') : void 0;
            return yield _this.signer.getAddress();
        })();
    }

    request(_ref) {
        const _this = this;

        return asyncToGenerator(function* () {
            const { method, params } = _ref;
            !_this.signer ? invariant(false, 'Signer is not defined') : void 0;

            if (method === 'eth_sendTransaction') {
                const sourceTx = params[0];
                const unsignedTx = yield convertToUnsigned(sourceTx);
                const signedTx = yield _this.signer.signTransaction(unsignedTx);
                return _this.send('eth_sendRawTransaction', [signedTx]);
            }

            if (method === 'eth_accounts') return [yield _this.getAddress()];
            return _this.send(method, params);
        })();
    }
}

export default LedgerHQProvider;
